import React, { useRef } from "react";
import styled from "@emotion/styled";
import { Grid, Typography } from "@mui/material";
import browseimg from "../../../../assets/images/browseimg.png";

const Wrapper = styled.section`
  width: 100%;
  padding: 60px 32px 120px;
  background: #fff;

  @media (max-width: 768px) {
    padding: 64px 20px 80px;
  }
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
`;

const BrowserWrap = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
`;

const BrowserImage = styled.img`
  width: 100%;
  display: block;
  border-radius: 6px;
  user-select: none;
`;

const Title = styled(Typography)`
  color: #000;
  font-size: 72px;
  line-height: 1;
  font-family: "primary1" !important;
  text-align: center;
  margin-top: -130px;
  position: relative;
  z-index: 2;

  @media (max-width: 900px) {
    font-size: 48px;
    margin-top: -34px;
  }

  @media (max-width: 520px) {
    font-size: 36px;
    margin-top: -18px;
  }
`;

const Intro = styled(Typography)`
  color: #000000;
  font-size: 18px;
  line-height: 1.25;
  text-align: center;
  max-width: 540px;
  margin: 20px auto 0;

  @media (max-width: 520px) {
    font-size: 14px;
    margin-top: 16px;
  }
`;

const CardsGrid = styled(Grid)`
  margin-top: 34px;

  @media (max-width: 768px) {
    margin-top: 24px;
  }
`;

const BrowseCard = styled.div`
  height: 100%;
  min-height: 176px;
  border-radius: 8px;
  padding: 32px;
  margin-top: 24px;
  background: #f8fafb65;
  box-shadow: 0 25px 48.2px 0 #c2dfece6 inset;
  will-change: transform;
  transition: box-shadow 0.4s ease;
  cursor: default;

  @media (max-width: 768px) {
    min-height: 150px;
    padding: 24px;
  }
`;

const CardTitle = styled(Typography)`
  color: #000;
  font-size: 24px;
  line-height: 1.12;
  font-family: "primary1" !important;
  margin-bottom: 18px;
  display: block;

  @media (max-width: 520px) {
    font-size: 20px;
    margin-bottom: 12px;
  }
`;

const CardText = styled(Typography)`
  color: #000;
  font-size: 18px;
  line-height: 1.25;
  max-width: 410px;
  display: block;

  @media (max-width: 520px) {
    font-size: 13px;
  }
`;

const browseCards = [
  {
    title: "Chat with any tab",
    text: "Ask Zephyr Agent to summarize webpages, generate content, translate, analyze text, and more. Without leaving the tab.",
  },
  {
    title: "Launch token in chat",
    text: "Launch tokens instantly on Clanker through Zephyr AI Agent with a single chat command.",
  },
  {
    title: "Maintain privacy",
    text: "Zephyr Agent doesn't retain or share chats, or use them for additional model training. No account or login is required. Just open and chat. Privately.",
  },
  {
    title: "Fast, accurate insights",
    text: "Zephyr Agent can analyze PDFs, Google Docs & Google Sheets, and more to help you get you the info you need. Faster.",
  },
];

const TiltCard = ({ card }) => {
  const cardRef = useRef(null);
  const frameRef = useRef(null);
  const motionRef = useRef({ rotateX: 0, rotateY: 0, targetX: 0, targetY: 0 });

  const lerp = (a, b, t) => a + (b - a) * t;

  const animate = () => {
    const m = motionRef.current;
    m.rotateX = lerp(m.rotateX, m.targetX, 0.1);
    m.rotateY = lerp(m.rotateY, m.targetY, 0.1);

    if (cardRef.current) {
      cardRef.current.style.transform = `perspective(600px) rotateX(${m.rotateX}deg) rotateY(${m.rotateY}deg)`;
    }

    if (
      Math.abs(m.rotateX - m.targetX) > 0.05 ||
      Math.abs(m.rotateY - m.targetY) > 0.05
    ) {
      frameRef.current = requestAnimationFrame(animate);
    } else {
      m.rotateX = m.targetX;
      m.rotateY = m.targetY;
      if (cardRef.current) {
        cardRef.current.style.transform = `perspective(600px) rotateX(${m.rotateX}deg) rotateY(${m.rotateY}deg)`;
      }
      frameRef.current = null;
    }
  };

  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;

    motionRef.current.targetY = x * 22;
    motionRef.current.targetX = y * 16;

    if (!frameRef.current) {
      frameRef.current = requestAnimationFrame(animate);
    }
  };

  const handleMouseLeave = () => {
    motionRef.current.targetX = 0;
    motionRef.current.targetY = 0;

    if (!frameRef.current) {
      frameRef.current = requestAnimationFrame(animate);
    }
  };

  return (
    <BrowseCard
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <CardTitle>{card.title}</CardTitle>
      <CardText>{card.text}</CardText>
    </BrowseCard>
  );
};

const Browsing = () => {
  return (
    <Wrapper data-aos="fade-up" data-aos-duration="900" id="AutonomousBrowsing">
      <Inner>
        <BrowserWrap>
          <BrowserImage src={browseimg} alt="Zephyr autonomous browsing preview" />
        </BrowserWrap>
        <Title data-aos="fade-up" data-aos-delay="100">Autonomous Browsing</Title>
        <Intro data-aos="fade-up" data-aos-delay="100">
          The smart AI assistant built right in your browser. Ask questions, get
          answers, with unparalleled privacy.
        </Intro>

        <CardsGrid container spacing={{ xs: 2, sm: 3 }} data-aos="fade-up" data-aos-delay="100">
          {browseCards.map((card) => (
            <Grid item xs={12} md={6} key={card.title}>
              <TiltCard card={card} />
            </Grid>
          ))}
        </CardsGrid>
      </Inner>
    </Wrapper>
  );
};

export default Browsing;